import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import type { ApplyCodesResult, Property, ReconLineLive, RentRollCheckResponse } from '../types';
import { StatusTag, fmtMoney } from './Reconciliations';

// A line is a usable code→lease mapping if it has a code and matched lease(s)
// (the name-matched tenant's active leases). Confident matches are pre-selected
// for seeding; ambiguous ones aren't.
const isMappable = (l: ReconLineLive): boolean =>
  !!l.custAccount && l.matchedTenantId !== null && l.systemLeaseIds.length > 0;
const isConfident = (l: ReconLineLive): boolean =>
  isMappable(l) && l.status !== 'ambiguous_match' && l.status !== 'invoiced_not_in_system';

export const RentRollCheckPage = () => {
  const [rentroll, setRentroll] = useState<File | null>(null);
  const [properties, setProperties] = useState<Property[]>([]);
  const [propertyId, setPropertyId] = useState<number | ''>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<RentRollCheckResponse | null>(null);
  // Selection is keyed by Sage code so each row toggles independently (two
  // codes can resolve to the same tenant — that's a conflict we surface).
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saveResult, setSaveResult] = useState<ApplyCodesResult | null>(null);

  useEffect(() => {
    api.properties().then(setProperties).catch(() => setProperties([]));
  }, []);

  const run = async () => {
    if (!rentroll || propertyId === '') return;
    setBusy(true);
    setError(null);
    setSaveResult(null);
    try {
      const res = await api.rentRollCheck(rentroll, propertyId);
      setData(res);
      // Pre-select confident mappings, but only the first code per tenant so we
      // never pre-create a two-codes-one-tenant conflict.
      const pre = new Set<string>();
      const seenTenant = new Set<number>();
      for (const l of res.result.lines.filter(isConfident)) {
        const tid = l.matchedTenantId as number;
        if (seenTenant.has(tid)) continue;
        seenTenant.add(tid);
        pre.add(l.custAccount as string);
      }
      setSelected(pre);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'check failed');
    } finally {
      setBusy(false);
    }
  };

  const toggle = (code: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      return next;
    });

  const saveCodes = async () => {
    if (!data) return;
    // The rent-roll match is tenant-level, so a ticked code lands on all of
    // that tenant's active leases in the property. Split-account tenants
    // (different codes per shop) must be set per lease in the lease dialog.
    const mappings = data.result.lines
      .filter((l) => isMappable(l) && selected.has(l.custAccount as string))
      .flatMap((l) =>
        l.systemLeaseIds.map((leaseId) => ({ leaseId, code: l.custAccount as string })),
      );
    if (mappings.length === 0) return;
    setBusy(true);
    try {
      setSaveResult(await api.applyCodes(mappings));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'saving codes failed');
    } finally {
      setBusy(false);
    }
  };

  const mappableLines = data ? data.result.lines.filter(isMappable) : [];

  // Tenants with more than one *selected* code — both codes would land on the
  // same lease(s) here, so the user must narrow the selection and set the
  // split codes per lease instead.
  const conflictTenantIds = (() => {
    const byTenant = new Map<number, number>();
    for (const l of mappableLines) {
      if (!selected.has(l.custAccount as string)) continue;
      const tid = l.matchedTenantId as number;
      byTenant.set(tid, (byTenant.get(tid) ?? 0) + 1);
    }
    return new Set([...byTenant.entries()].filter(([, n]) => n > 1).map(([tid]) => tid));
  })();

  return (
    <div className="page">
      <div className="toolbar">
        <Link to="/reconciliation">← Back to reconciliation</Link>
      </div>
      <h1>Rent Roll Check (ad-hoc)</h1>
      <p className="muted">
        Not part of the monthly Sage process. Upload a rent-roll workbook to see how the team's
        tracked figures compare to our system, and to seed lease Sage codes — the code the
        monthly CSV reconciliation matches on.
      </p>

      <div className="form-section">
        <div className="form-grid">
          <label className="field">
            <span>Rent roll workbook (.xlsx)</span>
            <input
              type="file"
              accept=".xlsx"
              onChange={(e) => setRentroll(e.target.files?.[0] ?? null)}
            />
          </label>
          <label className="field">
            <span>Property</span>
            <select
              value={propertyId}
              onChange={(e) => setPropertyId(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <option value="">Select a property…</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="toolbar">
          <button
            className="primary"
            disabled={!rentroll || propertyId === '' || busy}
            onClick={() => void run()}
          >
            {busy ? 'Checking…' : 'Run check'}
          </button>
        </div>
        {error && <div className="error">{error}</div>}
      </div>

      {data && (
        <>
          <h2>
            {data.propertyNameRaw} → {data.property.name}
          </h2>
          <div className="toolbar">
            <button
              className="primary"
              disabled={busy || selected.size === 0 || conflictTenantIds.size > 0}
              onClick={() => void saveCodes()}
            >
              Save {selected.size} Sage code{selected.size === 1 ? '' : 's'}
            </button>
            <span className="muted">
              Ticked rows write the rent-roll code onto the matched tenant's active lease(s).
              Review ambiguous matches before saving.
            </span>
          </div>
          {conflictTenantIds.size > 0 && (
            <div className="error">
              {conflictTenantIds.size} tenant(s) have more than one code ticked (highlighted below).
              Seeding writes a code to all of a tenant's active leases, so two codes would clash —
              untick all but one, then set the second code on the specific lease (Leases → Edit).
            </div>
          )}

          {saveResult && (
            <div className={saveResult.failed.length ? 'error' : 'muted'}>
              Saved {saveResult.applied.length} code(s).
              {saveResult.failed.length > 0 && (
                <ul>
                  {saveResult.failed.map((f, i) => (
                    <li key={i}>
                      {f.code} → lease {f.leaseId}: {f.error}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="table-wrap">
            <table className="grid">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>Set</th>
                  <th>Status</th>
                  <th>Code</th>
                  <th>Rent roll tenant</th>
                  <th>Matched system tenant</th>
                  <th>Match</th>
                  <th style={{ textAlign: 'right' }}>Roll rent</th>
                  <th style={{ textAlign: 'right' }}>System rent</th>
                  <th style={{ textAlign: 'right' }}>Diff</th>
                </tr>
              </thead>
              <tbody>
                {mappableLines.map((l) => {
                  const tid = l.matchedTenantId as number;
                  const code = l.custAccount as string;
                  const conflicted = selected.has(code) && conflictTenantIds.has(tid);
                  return (
                    <tr
                      key={`${code}-${tid}`}
                      style={conflicted ? { background: '#fdeaea' } : undefined}
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={selected.has(code)}
                          onChange={() => toggle(code)}
                        />
                      </td>
                      <td>
                        <StatusTag status={l.status} />
                      </td>
                      <td>{l.custAccount}</td>
                      <td>{l.excelTenantName ?? '—'}</td>
                      <td>{l.matchedTenantName ?? '—'}</td>
                      <td className="muted">
                        {l.matchMethod}
                        {l.matchScore !== null ? ` ${l.matchScore}` : ''}
                      </td>
                      <td style={{ textAlign: 'right' }}>{fmtMoney(l.csvRent)}</td>
                      <td style={{ textAlign: 'right' }}>{fmtMoney(l.systemRent)}</td>
                      <td style={{ textAlign: 'right' }}>{fmtMoney(l.rentDiff)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {mappableLines.length === 0 && (
            <p className="muted">No rent-roll rows could be matched to a system tenant.</p>
          )}
        </>
      )}
    </div>
  );
};